import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';

/**
 * Stack-based browser tab title overlay for popup dialogs.
 *
 * Unrelated to the ~50 direct `Title.setTitle()` call sites scattered across
 * routed page components and `/home` dashboard widgets (see
 * `docs/prompt-dialog-tab-title.md`) — those keep working exactly as before.
 * This service only layers dialog titles on top of whatever the current tab
 * title happens to be, and restores it on close. A page component that calls
 * `Title.setTitle()` directly while a dialog's title is pushed will have that
 * write silently overwritten when the dialog closes (see the prompt doc's
 * "Known limitation" section) — accepted, not fixed here.
 */
@Injectable({ providedIn: 'root' })
export class TabTitleService {

  private stack: string[] = [];
  private baseTitle: string | null = null;

  constructor(private titleService: Title) { }

  /** Pushes `title` onto the stack and applies it as the tab title. */
  pushTitle(title: string): void {
    if (this.stack.length === 0) {
      this.baseTitle = this.titleService.getTitle();
    }
    this.stack.push(title);
    this.titleService.setTitle(title);
  }

  /** Pops the top title and restores whatever was underneath it. No-op if the stack is empty. */
  popTitle(): void {
    if (this.stack.length === 0) return;
    this.stack.pop();
    const restored = this.stack.length > 0 ? this.stack[this.stack.length - 1] : this.baseTitle;
    if (restored != null) this.titleService.setTitle(restored);
  }
}
